import { computed, ref } from 'vue'
import type { Transaction } from '../types/transactionTypes'
import type { TransactionDetail } from '../types/transactionDetailTypes'

// Encapsulates the "select a transaction row, open the detail drawer" pattern
// used by the wallet's Recent Transactions card. The unified
// `/wallets/fiat/activity` feed is a 5-leg union (ledger + recharge requests
// + withdraw requests + investor rental payouts); the backend does not yet
// expose a unified `GET /wallets/fiat/activity/:id` detail endpoint, so the
// drawer renders entirely from the row data the table already has. When the
// backend lands a unified detail endpoint we plug a query hook back into
// `detail`/`detailLoading`/`detailError` here without changing consumers.
export function useTransactionDrawer() {
  const selectedTransaction = ref<Transaction | null>(null)
  const drawerOpen = ref(false)

  // Promote the row view-model into the richer `TransactionDetail` shape the
  // drawer consumes. Per-leg detail slices (withdrawalDetails / depositDetails
  // / investmentDetails / rentalDetails) stay null — the drawer's section
  // loop gracefully omits them.
  const detail = computed<TransactionDetail | null>(() => {
    const tx = selectedTransaction.value
    if (!tx) return null
    const createdAt = [tx.date, tx.time].filter(Boolean).join(' · ') || tx.date || null
    return {
      id: tx.referenceId ?? tx.id,
      displayTitle: tx.title,
      amount: tx.amount,
      currencyCode: null,
      transactionType: tx.incoming ? 'credit' : 'debit',
      status: tx.status,
      balanceBefore: null,
      balanceAfter: null,
      transactionReason: tx.category,
      createdAt,
      referenceId: tx.referenceId,
      propertyName: tx.propertyName ?? null,
      withdrawalDetails: null,
      investmentDetails: null,
      depositDetails: null,
      rentalDetails: null,
    }
  })

  const detailLoading = computed(() => false)
  const detailError = computed<unknown>(() => null)

  const openDrawer = (transaction: Transaction) => {
    selectedTransaction.value = transaction
    drawerOpen.value = true
  }

  const closeDrawer = () => {
    drawerOpen.value = false
  }

  return {
    selectedTransaction,
    drawerOpen,
    detail,
    detailLoading,
    detailError,
    openDrawer,
    closeDrawer,
  }
}
